'use client';

import { useState } from 'react';

import type { HousingPropertyDetail } from '@eliadmin/core/src/api/housing/housing-types';

import { HousingTabAttributes } from '@eliadmin/core/src/components/housing/detail/tabs/housing-tab-attributes';
import { HousingTabDescription } from '@eliadmin/core/src/components/housing/detail/tabs/housing-tab-description';
import { HousingTabGallery } from '@eliadmin/core/src/components/housing/detail/tabs/housing-tab-gallery';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function HousingTabs({ housing }: { housing: HousingPropertyDetail }) {
  const { t } = useTranslations();

  const allImages = [housing?.images, ...housing?.images];

  const tabs = [
    {
      title: t('text3766'),
      content: <HousingTabDescription housing={housing} />,
      show: !!housing?.description || housing?.short_description,
    },
    {
      title: t('text1399'),
      content: <HousingTabAttributes housing={housing} />,
      show: housing?.rooms[0]?.values && housing?.rooms[0]?.values?.length > 0,
    },
    {
      title: t('text2246'),
      content: <HousingTabGallery housing={housing} />,
      show: allImages && allImages?.length > 1,
    },
  ];

  const visibleTabs = tabs.filter(tab => tab.show);

  // Tracks only an explicit user click. The actually-displayed tab is derived
  // below from the current `visibleTabs` on every render, so it always
  // lands on the first *visible* tab and self-corrects once translations
  // finish loading (or `show` flags change) instead of freezing whatever
  // `tabs[0].title` happened to be at the very first render.
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const activeTab =
    selectedTab && visibleTabs.some(tab => tab.title === selectedTab)
      ? selectedTab
      : (visibleTabs[0]?.title ?? '');

  if (visibleTabs.length === 0) {
    return null;
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={setSelectedTab}
      className={cn(
        'w-full md:max-w-[690px]',
        'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
      )}
    >
      <TabsList className="flex h-fit w-full items-center justify-start gap-1 rounded-full bg-[#F2EEE7] p-1 shadow-none sm:gap-2 md:w-fit">
        {tabs.map(
          tab =>
            tab.show && (
              <TabsTrigger
                key={tab.title}
                value={tab.title}
                className={cn('w-full md:w-fit', 'ea-tabs-trigger__custom')}
              >
                {tab.title}
              </TabsTrigger>
            ),
        )}
      </TabsList>
      <TabsContent className="w-full" value={activeTab}>
        {tabs.find(tab => tab.title === activeTab)?.content}
      </TabsContent>
    </Tabs>
  );
}
